import { useState, type ReactNode } from 'react';
import {
  iqEntityForType,
  iqFieldOptions,
  resolveIqApprovalMappings,
  resolveIqMappings,
  setIqTarget,
  suggestIqField,
  type FieldDef,
  type FormSettings,
  type FormType,
  type IqMappingEntry,
} from '@jfh/intake-shared';
import { Icon } from '../../components/Icon.js';
import { IqTargetSelect } from '../IqTargetSelect.js';
import { MappingRow } from './MappingEditor.js';
import {
  FORM_TYPE_CARDS,
  STAGE1_CHANNELS,
  defaultChannel,
  defaultSourceForKind,
  describeMapping,
  endText,
  groupFieldsBySection,
  iqSummary,
  otherMappingIndexes,
  tableQuestions,
} from './logic.js';
import { useIqSpecFields } from '../../lib/useIqSpecFields.js';

export interface FormSettingsTabProps {
  type: FormType;
  settings: FormSettings;
  fields: FieldDef[];
  onTypeChange: (type: FormType) => void;
  onSettingsChange: (patch: Partial<FormSettings>) => void;
}

/**
 * Form settings (F9 P4, replaces builder/Settings.tsx): everyday first (what
 * the form is for, who approves it, after submit, privacy), then the iQ and
 * Wix connections collapsed. Every setting the old tab had is still here; the
 * expert mapping editor sits behind "Edit" on each value.
 */
export function FormSettingsTab({ type, settings, fields, onTypeChange, onSettingsChange }: FormSettingsTabProps) {
  const isCredit = type === 'credit_account';
  const isLead = iqEntityForType(type) === 'lead';

  return (
    <div className="scroll-y" style={{ flex: 1, background: 'var(--bg-app)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 28px 80px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card title="What is this form for?" sub="This decides who approves it and what it creates in iQ.">
          <div role="radiogroup" aria-label="Form type" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
            {FORM_TYPE_CARDS.map((c) => {
              const on = c.value === type;
              return (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => onTypeChange(c.value)}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: 'var(--r-lg)',
                    border: on ? '2px solid var(--accent)' : '1px solid var(--border-strong)',
                    background: on ? 'var(--surface-brand-tint)' : 'var(--bg-surface)',
                    color: 'var(--text-1)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13.5 }}>
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 99,
                        border: `2px solid ${on ? 'var(--accent)' : 'var(--border-strong)'}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 'none',
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: 99, background: on ? 'var(--accent)' : 'transparent' }} />
                    </span>
                    {c.label}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4, paddingLeft: 24 }}>{c.desc}</div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card title="Who approves it?" sub="What happens after an applicant presses Submit.">
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, flexWrap: 'wrap' }}>
            <FlowBox label="Start" tone="plain">
              <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>Applicant submits the form</div>
            </FlowBox>
            {!isLead && !settings.postToIqOnSubmit && (
              <>
                <Arrow />
                <FlowBox label={isCredit ? 'Stage 1 · checking the details' : 'Approval'} tone="accent">
                  <div style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '4px 0 6px' }}>Approve or decline in Teams</div>
                  <select
                    className="select"
                    style={{ height: 32, fontSize: 12.5 }}
                    value={settings.channel ?? ''}
                    aria-label="Approval channel"
                    onChange={(e) => onSettingsChange({ channel: e.target.value || undefined })}
                  >
                    <option value="">{defaultChannel(type)} (the usual)</option>
                    {[...STAGE1_CHANNELS, ...(settings.channel && !STAGE1_CHANNELS.includes(settings.channel) ? [settings.channel] : [])].map(
                      (c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ),
                    )}
                  </select>
                </FlowBox>
                {isCredit && (
                  <>
                    <Arrow />
                    <FlowBox label="Stage 2 · the credit decision" tone="accent">
                      <div style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '4px 0 6px' }}>Approve the credit in Teams</div>
                      <select
                        className="select"
                        style={{ height: 32, fontSize: 12.5 }}
                        value={settings.channel2 ?? 'Accounts'}
                        aria-label="Stage 2 channel"
                        onChange={(e) => onSettingsChange({ channel2: e.target.value })}
                      >
                        <option value="Accounts">Accounts</option>
                        <option value="Customer Service">Customer Service</option>
                      </select>
                    </FlowBox>
                  </>
                )}
              </>
            )}
            <Arrow />
            <FlowBox label="End" tone="brand">
              <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>{endText(type)}</div>
            </FlowBox>
          </div>
          <SwitchRow
            title="Skip approval"
            hint={
              isLead
                ? 'Lead forms always go straight to iQ.'
                : 'Create it in iQ as soon as it’s submitted, with no Teams approval.'
            }
            on={isLead || Boolean(settings.postToIqOnSubmit)}
            disabled={isLead}
            onToggle={() => onSettingsChange({ postToIqOnSubmit: !settings.postToIqOnSubmit })}
            border
          />
          {!isLead && (
            <div className="field-help" style={{ marginTop: 10 }}>
              Who can approve in each channel is set in Settings → Teams &amp; approvals.
            </div>
          )}
        </Card>

        <Card title="After they submit" sub="What the applicant sees once they’ve sent the form.">
          <label className="field-label">Thank-you message</label>
          <textarea
            className="textarea"
            rows={2}
            placeholder="Thanks — we've received your application and will be in touch shortly."
            value={settings.success ?? ''}
            onChange={(e) => onSettingsChange({ success: e.target.value })}
          />
        </Card>

        <Card title="Privacy and spam">
          <label className="field-label">Privacy note under the Submit button</label>
          <textarea
            className="textarea"
            rows={3}
            placeholder="By submitting, you agree to JFH Horticultural storing your details to process this application, in line with our privacy policy."
            value={settings.consentText ?? ''}
            onChange={(e) => onSettingsChange({ consentText: e.target.value })}
          />
          <SwitchRow
            title="They must tick to agree"
            hint="Adds a tick box to the privacy note."
            on={Boolean(settings.consentRequired)}
            onToggle={() => onSettingsChange({ consentRequired: !settings.consentRequired })}
          />
          <SwitchRow
            title="Check applicants are real people"
            hint="Cloudflare Turnstile. Usually invisible. Leave this on."
            on={settings.turnstile !== false}
            onToggle={() => onSettingsChange({ turnstile: settings.turnstile === false })}
          />
        </Card>

        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '.12em',
            color: 'var(--jfh-cactus)',
            textTransform: 'uppercase',
            marginTop: 8,
          }}
        >
          Connections
        </div>

        <IqConnection type={type} settings={settings} fields={fields} onSettingsChange={onSettingsChange} />

        <FoldCard
          icon="link"
          title="Wix website form"
          summary={settings.wixFormId ? 'Linked to a Wix form.' : 'Not linked. Only needed for forms filled in on the Wix site.'}
        >
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
            Only if this form is filled in on the Wix site instead of our own form. Wix submissions then come in here and
            follow the same approval.
          </p>
          <div>
            <label className="field-label">Wix form ID</label>
            <input
              className="input mono"
              placeholder="e.g. 8bf86a10-ae4c-…"
              value={settings.wixFormId ?? ''}
              onChange={(e) => onSettingsChange({ wixFormId: e.target.value.trim() || undefined })}
            />
          </div>
          <div className="field-help" style={{ marginTop: 0 }}>
            The Wix webhook itself is set up once, in Settings → Connections.
            {settings.wixFormId ? ' Next, open each question and fill in its Wix field under Connections.' : ''}
          </div>
        </FoldCard>
      </div>
    </div>
  );
}

/** "Sending answers to iQ": the question → iQ field table, other values, and stage 2. */
function IqConnection({
  type,
  settings,
  fields,
  onSettingsChange,
}: {
  type: FormType;
  settings: FormSettings;
  fields: FieldDef[];
  onSettingsChange: (patch: Partial<FormSettings>) => void;
}) {
  const [raw, setRaw] = useState(false);
  const isCredit = type === 'credit_account';
  const entity = iqEntityForType(type);
  const entries = resolveIqMappings(settings);
  const approval = resolveIqApprovalMappings(settings);
  const liveFields = useIqSpecFields();
  const options = iqFieldOptions(type, settings, liveFields);
  const used = new Set(entries.map((m) => m.target));
  const questions = tableQuestions(fields, type);
  const others = otherMappingIndexes(entries, new Set(questions.map((f) => f.id)));
  const targetOf = (id: string) =>
    entries.find((m) => m.source.type === 'field' && m.source.fieldId === id)?.target ?? '';
  const rows = questions.map((f) => {
    const target = targetOf(f.id);
    const suggestion = target || ['consent', 'hidden', 'file'].includes(f.type) ? '' : suggestIqField(f.label, options);
    return { f, target, suggestion };
  });
  const mapped = rows.filter((r) => r.target).length;
  const suggested = rows.filter((r) => r.suggestion).length;
  const contactsAuto = entity !== 'lead' && fields.some((f) => f.type === 'contact');
  const mappable = fields.filter((f) => f.type !== 'heading');
  const groups = groupFieldsBySection(fields);

  const commit = (next: IqMappingEntry[]) => onSettingsChange({ iqMappings: next, iqMapping: undefined });
  const commitApproval = (next: IqMappingEntry[]) => onSettingsChange({ iqApprovalMappings: next });

  return (
    <FoldCard
      icon="db"
      title="Sending answers to iQ"
      summary={iqSummary(mapped, rows.length, others.length, contactsAuto)}
      badge={suggested ? `${suggested} suggested` : undefined}
    >
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
        When an application is approved we create a {entity} in iQ. Pick which iQ field each answer fills.
        {contactsAuto ? ' Contacts go to iQ’s contact list automatically, main contact first.' : ''}
      </p>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr)',
            gap: 10,
            padding: '8px 12px',
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text-3)',
            textTransform: 'uppercase',
            letterSpacing: '.05em',
          }}
        >
          <span>Question on the form</span>
          <span>iQ field</span>
        </div>
        {rows.length === 0 && (
          <div style={{ padding: 12, fontSize: 12.5, color: 'var(--text-3)' }}>No questions to send yet.</div>
        )}
        {rows.map(({ f, target, suggestion }) => (
          <div
            key={f.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr)',
              gap: 10,
              padding: '7px 12px',
              borderBottom: '1px solid var(--border)',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
              {f.audience === 'staff' && (
                <span className="badge neutral" style={{ height: 18, fontSize: 10.5, padding: '0 6px', flex: 'none' }}>
                  Staff
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 140px', minWidth: 0, display: 'grid', gap: 4 }}>
                <IqTargetSelect
                  compact
                  value={target}
                  options={options}
                  used={used}
                  emptyLabel="Not sent"
                  ariaLabel={`iQ field for ${f.label}`}
                  onChange={(t) => onSettingsChange(setIqTarget(settings, 'iqMappings', f.id, t))}
                />
              </div>
              {suggestion && (
                <button
                  type="button"
                  className="btn sm"
                  style={{ height: 30, flex: 'none', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title="Use the suggested iQ field"
                  onClick={() => onSettingsChange(setIqTarget(settings, 'iqMappings', f.id, suggestion))}
                >
                  {suggestion}?
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>Other values sent to iQ</div>
        <div style={{ display: 'grid', gap: 6 }}>
          {others.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>None yet.</div>
          )}
          {others.map((i) => (
            <ValueRow
              key={i}
              entry={entries[i]!}
              fields={mappable}
              groups={groups}
              placeholder="EmailAddress"
              onChange={(e) => commit(entries.map((x, j) => (j === i ? e : x)))}
              onRemove={() => commit(entries.filter((_, j) => j !== i))}
            />
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn sm"
              onClick={() => commit([...entries, { target: '', source: { type: 'const', value: '', valueType: 'string' } }])}
            >
              <Icon name="plus" size={14} /> Always send a fixed value
            </button>
            <button
              type="button"
              className="btn sm"
              onClick={() =>
                commit([
                  ...entries,
                  {
                    target: '',
                    source: defaultSourceForKind(mappable.some((f) => f.type === 'contact') ? 'contact' : 'derived', mappable),
                  },
                ])
              }
            >
              <Icon name="plus" size={14} /> Something else
            </button>
            <button type="button" className="btn ghost sm" onClick={() => setRaw((r) => !r)} aria-expanded={raw}>
              {raw ? 'Hide the technical view' : 'Show the technical view'}
            </button>
          </div>
          {raw && (
            <pre
              className="mono"
              style={{
                margin: 0,
                padding: 12,
                background: 'var(--bg-sunken)',
                borderRadius: 'var(--r-md)',
                fontSize: 11.5,
                maxHeight: 260,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
              }}
            >
              {JSON.stringify(isCredit ? { iqMappings: entries, iqApprovalMappings: approval } : entries, null, 2)}
            </pre>
          )}
        </div>
      </div>

      {isCredit && (
        <div>
          <div style={{ fontWeight: 800, fontSize: 13 }}>When Accounts approve the credit (stage 2)</div>
          <p style={{ margin: '4px 0 8px', fontSize: 12.5, color: 'var(--text-2)' }}>
            These update the customer already created in iQ. Accounts’ own questions are set from each question’s
            Connections. Leave empty to send only the credit status and limit.
          </p>
          <div style={{ display: 'grid', gap: 6 }}>
            {approval.map((m, i) => (
              <ValueRow
                key={i}
                entry={m}
                fields={mappable}
                groups={groups}
                placeholder="CreditControlSetup.CreditLimit"
                onChange={(e) => commitApproval(approval.map((x, j) => (j === i ? e : x)))}
                onRemove={() => commitApproval(approval.filter((_, j) => j !== i))}
              />
            ))}
            <div>
              <button
                type="button"
                className="btn sm"
                onClick={() =>
                  commitApproval([...approval, { target: '', source: { type: 'const', value: '', valueType: 'string' } }])
                }
              >
                <Icon name="plus" size={14} /> Add a value for stage 2
              </button>
            </div>
          </div>
        </div>
      )}
    </FoldCard>
  );
}

/** One non-question value as a sentence; a fixed value edits inline, anything else opens the full editor. */
function ValueRow({
  entry,
  fields,
  groups,
  placeholder,
  onChange,
  onRemove,
}: {
  entry: IqMappingEntry;
  fields: FieldDef[];
  groups: ReturnType<typeof groupFieldsBySection>;
  placeholder: string;
  onChange: (e: IqMappingEntry) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(!entry.target);
  const d = describeMapping(entry, fields);
  const src = entry.source;
  return (
    <div
      style={{
        padding: '8px 12px',
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        fontSize: 12.5,
        color: 'var(--text-2)',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}>
          <Icon name={d.icon} size={14} />
        </span>
        <span style={{ flex: 1, minWidth: 200 }}>
          {src.type === 'const' ? 'Always send' : d.text.replace(/ to$/, '')}
          {src.type === 'const' && (
            <input
              className="input"
              style={{ height: 28, width: 130, fontSize: 12, margin: '0 6px', display: 'inline-block' }}
              value={src.value}
              aria-label="Fixed value"
              onChange={(e) => onChange({ ...entry, source: { ...src, value: e.target.value } })}
            />
          )}
          {src.type === 'const' ? 'to ' : ' to '}
          <span className="mono" style={{ color: 'var(--text-1)' }}>
            {entry.target || '(choose an iQ field)'}
          </span>
        </span>
        <button type="button" className="btn ghost sm" onClick={() => setEditing((x) => !x)} aria-expanded={editing}>
          {editing ? 'Done' : 'Edit'}
        </button>
        <button type="button" className="icon-btn" style={{ width: 26, height: 26 }} onClick={onRemove} aria-label="Remove">
          <Icon name="x" size={13} />
        </button>
      </div>
      {editing && (
        <div style={{ marginTop: 6 }}>
          <MappingRow
            entry={entry}
            fields={fields}
            fieldGroups={groups}
            targetPlaceholder={placeholder}
            onTargetChange={(target) => onChange({ ...entry, target })}
            onKindChange={(kind) => onChange({ ...entry, source: defaultSourceForKind(kind, fields) })}
            onSourceChange={(source) => onChange({ ...entry, source })}
            onRemove={onRemove}
          />
        </div>
      )}
    </div>
  );
}

function Card({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <div className="card" style={{ padding: 20, display: 'grid', gap: 12 }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>{title}</h3>
        {sub && <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-2)' }}>{sub}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** A connection card: collapsed to a one-line summary until opened. */
function FoldCard({
  icon,
  title,
  summary,
  badge,
  children,
}: {
  icon: string;
  title: string;
  summary: string;
  badge?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 20px',
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          color: 'var(--text-1)',
        }}
      >
        <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}>
          <Icon name={icon} size={18} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 800, fontSize: 15 }}>{title}</span>
          <br />
          <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{summary}</span>
        </span>
        {badge && <span className="badge pending">{badge}</span>}
        <span style={{ transform: `rotate(${open ? 180 : 0}deg)`, transition: 'transform .14s', color: 'var(--text-3)', display: 'inline-flex' }}>
          <Icon name="chevron" size={16} />
        </span>
      </button>
      {open && <div style={{ padding: '0 20px 20px', display: 'grid', gap: 14 }}>{children}</div>}
    </div>
  );
}

function FlowBox({ label, tone, children }: { label: string; tone: 'plain' | 'accent' | 'brand'; children: ReactNode }) {
  const look = {
    plain: { flex: 1, background: 'var(--bg-surface-2)', border: '1px solid var(--border)' },
    accent: { flex: 1.4, background: 'var(--bg-surface)', border: '1px solid var(--accent)' },
    brand: { flex: 1, background: 'var(--surface-brand-tint)', border: '1px solid var(--border-brand-soft)' },
  }[tone];
  return (
    <div style={{ ...look, minWidth: tone === 'accent' ? 150 : 110, padding: 12, borderRadius: 'var(--r-lg)' }}>
      <div className="mono rdr-step-label" style={{ fontSize: 10 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Arrow() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-3)' }} aria-hidden="true">
      <Icon name="chevronR" size={16} />
    </div>
  );
}

function SwitchRow({
  title,
  hint,
  on,
  disabled,
  onToggle,
  border,
}: {
  title: string;
  hint: string;
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
  border?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 14,
        ...(border ? { paddingTop: 14, borderTop: '1px solid var(--border)' } : {}),
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{hint}</div>
      </div>
      <button
        type="button"
        className={'toggle' + (on ? ' on' : '')}
        role="switch"
        aria-checked={on}
        aria-label={title}
        disabled={disabled}
        onClick={onToggle}
      />
    </div>
  );
}
